// The web address decides the shop (spec section 8 "Hosts", plan Task A4). Pure: no database here. A request's host
// and path resolve against the pressroom.storefront_hosts rows to a shop and the base path its links are built on:
//   * the slug host (apparel.shop.bagcarriers.com): the first path segment is the shop slug, the base path "/<slug>";
//   * /_shop/<slug>/ on any host: the shop slug, base path "/_shop/<slug>" (the site that owns a domain proxies its
//     mount, say /apparel/*, here);
//   * otherwise the exact host and the longest base_path that prefixes the path. A row with no storefront is a
//     back-end or old-links host (pressroom.bagcarriers.dev): known, with no shop of its own.
// An unknown host resolves to nothing, and nothing of any brand leaks to it.
import { SHOP_SLUG } from "./types.ts";

export const SLUG_HOST = "apparel.shop.bagcarriers.com";
const MOUNT = /^\/_shop\/([a-z][a-z0-9-]{1,30})(?=\/|$)/;

export interface HostRow {
  host: string;
  base_path: string;
  storefront_id: string | null;
  is_primary: boolean;
}

export interface ResolvedHost {
  // Null: a known host with no shop of its own (the back end), whose /design falls back to the public shop setting.
  storefrontId: string | null;
  // The path every emitted link on this request is prefixed with ("" on a shop's own host).
  basePath: string;
  known: true;
}

// The host the buyer typed, lower case, without a port: x-forwarded-host (Netlify in front of a proxy), then host,
// then the URL's own.
export function hostOf(req: Request): string {
  const raw = req.headers.get("x-forwarded-host") ?? req.headers.get("host") ?? new URL(req.url).host;
  return raw.split(",")[0]!.trim().replace(/:\d+$/, "").toLowerCase();
}

export const pathOf = (req: Request): string => new URL(req.url).pathname;

const under = (path: string, base: string) => base === "" || path === base || path.startsWith(`${base}/`);

export function resolveHost(rows: readonly HostRow[], host: string, path: string): ResolvedHost | null {
  const h = host.toLowerCase();
  const mount = MOUNT.exec(path);
  if (mount) return { storefrontId: mount[1]!, basePath: `/_shop/${mount[1]}`, known: true };
  if (h === SLUG_HOST) {
    const slug = path.split("/")[1] ?? "";
    return SHOP_SLUG.test(slug) ? { storefrontId: slug, basePath: `/${slug}`, known: true } : null;
  }
  const hit = rows.filter((r) => r.host === h && under(path, r.base_path)).sort((a, b) => b.base_path.length - a.base_path.length)[0];
  return hit ? { storefrontId: hit.storefront_id, basePath: hit.base_path, known: true } : null;
}

// A shop's public base: its primary host and path, else the public_base_url column (Phase 1 rows), else the slug host.
export function publicBaseFor(slug: string, primary: HostRow | null, column: string | null): string {
  if (primary) return `https://${primary.host}${primary.base_path}`;
  if (column) return column.replace(/\/+$/, "");
  return `https://${SLUG_HOST}/${slug}`;
}
